import * as Dialog from '@radix-ui/react-dialog';
import { ExternalLink, LogIn, X } from 'lucide-react';
import { faengtAussenklickAb } from '@/onboarding/tourSchutz';

// Einen weiteren Nutzer anmelden — und warum das am Telefon zwei
// Schritte braucht.
//
// Befund K5 vom Gerätetest: In der installierten App erschien im
// Anmeldedialog immer wieder derselbe Nextcloud-Nutzer; ein Wechsel war
// unmöglich, und ein Abmelden in Safari half nicht.
//
// Der Grund liegt an zwei Stellen, und keine davon können wir von hier
// aus aufheben:
//
//   1. iOS führt für eine installierte App einen EIGENEN Zwischenspeicher
//      für Anmeldungen. Was in Safari passiert, gilt hier nicht.
//   2. Nextclouds OAuth2-App kennt keinen Weg, eine Neuanmeldung zu
//      verlangen. Ihr Authorize-Endpunkt liest client_id, state,
//      response_type und redirect_uri — kein `prompt`, wie es OpenID
//      Connect dafür vorsieht. Besteht drüben eine Anmeldung, führt sie
//      unbesehen zu deren Nutzer.
//      Und `/logout` ist bei Nextcloud gegen fremde Aufrufe geschützt
//      (kein NoCSRFRequired); ein Aufruf von hier aus scheitert an einem
//      Token, das nur Nextclouds eigene Seiten mitbringen.
//
// Bleibt der ehrliche Weg: den Menschen einmal zu Nextcloud schicken,
// wo er sich mit den dortigen Mitteln abmeldet, und ihn dann
// zurückholen. Wichtig dabei — die Adresse wird IM SELBEN Fenster
// geöffnet, nicht in einem neuen: nur so teilt sie den Zwischenspeicher
// der installierten App. Ein `target="_blank"` würde auf dem iPhone
// Safari aufmachen, und dort abzumelden ist genau das, was nicht wirkt.

/**
 * Der Wortlaut, unter dem der Ablauf in beiden Rahmen auftaucht — als
 * Menüeintrag und als Überschrift der Anleitung.
 *
 * Als Konstante, weil genau hier die beiden Rahmen schon einmal
 * auseinandergelaufen sind: am Schreibtisch hiess der Eintrag „Nutzer
 * hinzufügen" und führte in eine Sackgasse, mobil „Weiteren Nutzer
 * anmelden" und in diese Anleitung (P4). Ein geteilter String macht die
 * Angleichung strukturell statt gleich getippt.
 */
export const NUTZER_HINZUFUEGEN = 'Weiteren Nutzer anmelden';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Adresse der Nextcloud, aus der Konfiguration (settings: nc_url). */
  ncUrl: string | null;
  /** Startet den Anmeldeablauf. */
  onAnmelden: () => void;
};

export function NutzerHinzufuegen({ open, onClose, ncUrl, onAnmelden }: Props) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[75] bg-ink/30" />
        <Dialog.Content
          onPointerDownOutside={faengtAussenklickAb}
          onInteractOutside={faengtAussenklickAb}
          aria-describedby={undefined}
          data-ueber-tour=""
          className="dialog-mobil-vollbild fixed left-1/2 top-1/2 z-[75] w-[min(30rem,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-md border border-line bg-cream p-5 shadow-lg"
        >
          <div className="flex items-start justify-between gap-4">
            <Dialog.Title className="font-serif text-lg text-ink">
              {NUTZER_HINZUFUEGEN}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Schließen"
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded text-ink-soft hover:bg-cream-dark"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          <ol className="mt-4 space-y-4">
            <li>
              <p className="text-sm text-ink">
                <span className="font-medium">1. Ist bei Nextcloud noch jemand anderes angemeldet?</span>
              </p>
              <p className="mt-1 text-sm text-ink-soft">
                Dann melde Dich zuerst bei Nextcloud ab — sonst kommt hier wieder
                derselbe Name an. Auf diesem Gerät merkt sich die App die
                Nextcloud-Anmeldung getrennt vom Browser.
              </p>
              {ncUrl ? (
                <a
                  href={ncUrl}
                  // Bewusst OHNE target="_blank": siehe Kopf dieser Datei.
                  className="mt-2 inline-flex min-h-[44px] items-center gap-2 rounded-md border border-line bg-cream-dark px-4 py-2 text-sm text-ink hover:bg-gold-soft"
                >
                  <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
                  Zu Nextcloud und dort abmelden
                </a>
              ) : (
                <p className="mt-2 text-sm text-coral">
                  Die Adresse der Nextcloud ist nicht hinterlegt — bitte beim Admin melden.
                </p>
              )}
            </li>

            <li>
              <p className="text-sm text-ink">
                <span className="font-medium">2. Hier anmelden</span>
              </p>
              <p className="mt-1 text-sm text-ink-soft">
                Danach zurück in diese App und die Anmeldung starten. Die bisherige
                Sitzung bleibt bestehen; Du kannst später zwischen beiden wechseln.
              </p>
              <button
                type="button"
                onClick={onAnmelden}
                className="mt-2 inline-flex min-h-[44px] items-center gap-2 rounded-md bg-akzent px-4 py-2 text-sm font-medium text-cream hover:bg-akzent-stark"
              >
                <LogIn className="h-4 w-4 shrink-0" aria-hidden />
                Anmeldung starten
              </button>
            </li>
          </ol>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
